import { useCallback, useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import type { ApiClient, CctvDetail, CctvWerte, CctvZeile } from '../api';
import { SymKamera } from '../ElektroSymbole';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, EmptyState, ErrorState, Feld, FormSection, LoadingState,
  PropertyGrid, StatusBadge, type Kennwert,
} from '../muster';
import {
  CctvSchrankSchema, Datenschutzhinweis, STATUS_TEXT, herkunftText, statusZustand,
} from './ElektroCctv';

interface Rahmen { api: ApiClient; projekt?: { id: string } | null }

/**
 * Video / CCTV — eine Kamera (Etappe 2, docs/CCTV.md).
 *
 * Oben der geschriebene Stand (Stammdaten, Zuordnung, Kabel, PoE), darunter
 * die Befunde und die PoE-Bilanz ihres Switches aus einem PROBELAUF — der
 * Stand des Modells, nicht der zuletzt geschriebene. Unten die Handpflege:
 * Was hier gesetzt wird, trägt die Herkunft „von Hand" und bleibt beim
 * nächsten Lauf stehen; „an den Lauf zurückgeben" leert den Wert wieder.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Das Formular — ohne React, deshalb einzeln prüfbar
// ═══════════════════════════════════════════════════════════════════════════

export interface CctvFormular {
  laengeM: number | null;
  kameraart: string;
  poeW: number | null;
  poeKlasse: string;
  vlan: number | null;
  ipPlan: string;
  oeffnungswinkel: number | null;
  blickrichtung: number | null;
  bemerkung: string;
}

export function formularAus(z: CctvZeile): CctvFormular {
  return {
    laengeM: z.laengeM,
    kameraart: z.kameraart ?? '',
    poeW: z.poeW,
    poeKlasse: z.poeKlasse ?? '',
    vlan: z.vlan,
    ipPlan: z.ipPlan ?? '',
    oeffnungswinkel: z.oeffnungswinkel,
    blickrichtung: z.blickrichtung,
    bemerkung: z.bemerkung ?? '',
  };
}

/**
 * Nur was sich geändert hat. Ein geleertes Feld geht als `leeren` hinaus —
 * NULL hieße bei der API „unverändert". Die PoE-Klasse reist mit den Watt:
 * Die API setzt sie nur zusammen mit einem PoE-Wert.
 */
export function aenderungen(alt: CctvFormular, neu: CctvFormular): CctvWerte | null {
  const w: CctvWerte = {};
  const leeren: string[] = [];

  const zahl = (feld: 'laengeM' | 'vlan' | 'oeffnungswinkel' | 'blickrichtung',
                leerName: string = feld) => {
    if (neu[feld] === alt[feld]) return;
    if (neu[feld] === null) leeren.push(leerName);
    else w[feld] = neu[feld];
  };

  const text = (feld: 'kameraart' | 'ipPlan' | 'bemerkung') => {
    const n = neu[feld].trim();
    if (n === alt[feld].trim()) return;
    if (n === '') leeren.push(feld);
    else w[feld] = n;
  };

  zahl('laengeM');
  zahl('vlan');
  zahl('oeffnungswinkel');
  zahl('blickrichtung');
  text('kameraart');
  text('ipPlan');
  text('bemerkung');

  const poeGeaendert = neu.poeW !== alt.poeW || neu.poeKlasse.trim() !== alt.poeKlasse.trim();

  if (poeGeaendert) {
    if (neu.poeW === null) {
      leeren.push('poe');
    } else {
      w.poeW = neu.poeW;
      if (neu.poeKlasse.trim() !== '') w.poeKlasse = neu.poeKlasse.trim();
    }
  }

  if (leeren.length > 0) w.leeren = leeren;

  return Object.keys(w).length > 0 ? w : null;
}

/** Prüfungen vor dem Senden — dieselben Grenzen wie die API. */
export function formularFehler(f: CctvFormular): Partial<Record<keyof CctvFormular, string>> {
  const fehler: Partial<Record<keyof CctvFormular, string>> = {};

  if (f.laengeM !== null && (f.laengeM <= 0 || f.laengeM > 1000))
    fehler.laengeM = 'größer 0 und höchstens 1000 m';
  if (f.vlan !== null && (!Number.isInteger(f.vlan) || f.vlan < 1 || f.vlan > 4094))
    fehler.vlan = 'ganze Zahl von 1 bis 4094';
  if (f.oeffnungswinkel !== null && (f.oeffnungswinkel <= 0 || f.oeffnungswinkel > 360))
    fehler.oeffnungswinkel = 'größer 0 und höchstens 360°';
  if (f.blickrichtung !== null && (f.blickrichtung < 0 || f.blickrichtung >= 360))
    fehler.blickrichtung = '0 bis unter 360°';
  if (f.poeW !== null && (f.poeW < 0 || f.poeW > 100))
    fehler.poeW = '0 bis 100 W';

  return fehler;
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Ansicht einer Kamera
// ═══════════════════════════════════════════════════════════════════════════

const STUFE_ZUSTAND = { fehler: 'fehler', warnung: 'warnung', hinweis: 'info' } as const;

export function CctvKamera({
  detail, beiSpeichern, speichert = false,
}: {
  detail: CctvDetail;
  beiSpeichern: (werte: CctvWerte) => void;
  speichert?: boolean;
}) {
  const z = detail.zeile;
  const ausgang = formularAus(z);
  const [formular, setFormular] = useState<CctvFormular>(ausgang);

  // Neu geladen (nach dem Speichern) - das Formular folgt dem Stand.
  useEffect(() => { setFormular(formularAus(detail.zeile)); }, [detail.zeile]);

  const fehler = formularFehler(formular);
  const werte = aenderungen(ausgang, formular);

  const setzeZahl = (feld: 'laengeM' | 'poeW' | 'vlan' | 'oeffnungswinkel' | 'blickrichtung') =>
    (w: unknown) => setFormular((f) => ({ ...f, [feld]: typeof w === 'number' ? w : null }));

  const setzeText = (feld: 'kameraart' | 'poeKlasse' | 'ipPlan' | 'bemerkung') =>
    (w: unknown) => setFormular((f) => ({ ...f, [feld]: typeof w === 'string' ? w : '' }));

  const zurueck = (feld: string) => beiSpeichern({ leeren: [feld] });

  const stamm: Kennwert[] = [
    { beschriftung: 'Kennung', wert: z.kennung, hinweis: 'fest, wird nie wiederverwendet' },
    { beschriftung: 'IT-Kennung', wert: z.itKennung ?? '—', hinweis: 'aus der IT-Zuweisung' },
    { beschriftung: 'Geschoss', wert: z.geschoss ?? '—' },
    { beschriftung: 'Raum', wert: [z.raumnummer, z.raumname].filter(Boolean).join(' — ') || '—' },
    { beschriftung: 'Familie', wert: z.familie ?? '—', herkunft: 'modell' },
    { beschriftung: 'Typ', wert: z.typ ?? '—', herkunft: 'modell' },
    { beschriftung: 'Kameraart', wert: z.kameraart ?? '—' },
    { beschriftung: 'Kameraart aus', wert: herkunftText(z.kameraartHerkunft) },
    {
      beschriftung: 'Blickfeld',
      wert: z.oeffnungswinkel === null && z.blickrichtung === null
        ? '—'
        : `${z.oeffnungswinkel ?? '–'}° Öffnung, Blick ${z.blickrichtung ?? '–'}°`,
      hinweis: 'Blickrichtung: 0° = Projekt-Nord, im Uhrzeigersinn',
    },
  ];

  const zuordnung: Kennwert[] = [
    { beschriftung: 'IT-Schrank', wert: z.itSchrank ?? 'ohne Schrank' },
    { beschriftung: 'Schrank über', wert: herkunftText(z.schrankHerkunft) },
    {
      beschriftung: 'Patchfeld / Port',
      wert: z.patchfeldNr ? `${z.patchfeldNr} / ${z.patchfeldPort ?? '–'}` : '—',
    },
    {
      beschriftung: 'Switch / Port',
      wert: z.switchNr ? `${z.switchNr} / ${z.switchPort ?? '–'}` : '—',
      hinweis: 'Sicherheitstechnik auf eigenen Switches (Festlegung G1)',
    },
    { beschriftung: 'Leitungsart', wert: z.leitungsart ?? '—' },
    { beschriftung: 'VLAN (Plan)', wert: z.vlan ?? '—' },
    { beschriftung: 'IP (Plan)', wert: z.ipPlan ?? '—' },
  ];

  const kabel: Kennwert[] = [
    { beschriftung: 'Länge', wert: z.laengeM, einheit: 'm', nachkomma: 1 },
    { beschriftung: 'Länge aus', wert: herkunftText(z.laengeHerkunft) },
    { beschriftung: 'Rechenweg', wert: z.laengeRechenweg ?? '—', breit: true },
    {
      beschriftung: 'Grenzen', breit: true,
      wert: `höchstens ${detail.regeln.kabelMaxM} m (K01), Warnung über `
        + `${detail.regeln.kabelWarnM} m (K02); Schätzung = (|dx| + |dy|) × `
        + `${detail.regeln.umwegfaktor} + Höhe + ${detail.regeln.zuschlagM} m`,
    },
  ];

  if (detail.probelauf && detail.probelauf.laengeM !== z.laengeM)
    kabel.push({
      beschriftung: 'Probelauf', breit: true,
      wert: detail.probelauf.laengeRechenweg,
      hinweis: 'was der nächste Lauf schreiben würde',
    });

  const b = detail.switchBilanz;

  const poe: Kennwert[] = [
    { beschriftung: 'PoE', wert: z.poeW, einheit: 'W', nachkomma: 1,
      hinweis: 'Ansatz an der Speisung (PSE) — kein Messwert' },
    { beschriftung: 'PoE aus', wert: herkunftText(z.poeHerkunft) },
    { beschriftung: 'PoE-Klasse', wert: z.poeKlasse ?? '—' },
    {
      beschriftung: b ? `${b.schrank} ${b.switch}` : 'Switch',
      wert: b
        ? (
          <StatusBadge
            zustand={b.ueberschritten ? 'fehler' : 'gut'}
            text={`${b.poeW.toFixed(1)} von ${b.budgetW.toFixed(1)} W`}
            klein
          />
        )
        : 'kein Switch zugeordnet',
      hinweis: b
        ? `${b.geraete} Geräte, davon ${b.kameras} Kameras`
          + (b.sicherheit ? ' — Sicherheitsswitch' : '')
        : undefined,
    },
  ];

  return (
    <>
      <ContentSection titel="Stammdaten">
        <PropertyGrid eintraege={stamm} />
      </ContentSection>

      <ContentSection titel="Zuordnung">
        <PropertyGrid eintraege={zuordnung} />
      </ContentSection>

      <ContentSection titel="Kabel">
        <PropertyGrid eintraege={kabel} />
      </ContentSection>

      <ContentSection titel="PoE">
        <PropertyGrid eintraege={poe} />
      </ContentSection>

      <ContentSection
        titel="Befunde"
        beschreibung="aus einem Probelauf — der Stand des Modells"
        meta={`${detail.befunde.length}`}
      >
        {detail.befunde.length === 0 ? (
          <EmptyState titel="Keine Befunde." klein />
        ) : (
          <ul className="cctv-befunde" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {detail.befunde.map((x, i) => (
              <li key={`${x.regel}-${i}`} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <StatusBadge
                  zustand={STUFE_ZUSTAND[x.stufe as keyof typeof STUFE_ZUSTAND] ?? 'info'}
                  text={x.regel}
                  klein
                />
                <span style={{ fontSize: 13 }}>{x.text}</span>
              </li>
            ))}
          </ul>
        )}
      </ContentSection>

      <FormSection
        titel="Handpflege"
        beschreibung="Gesetzte Werte tragen die Herkunft „von Hand“ und bleiben beim nächsten Lauf stehen."
        gesperrt={z.entfernt}
        gesperrtGrund="Die Kamera ist im Modell entfallen."
        fuss={(
          <>
            <button
              type="button"
              className="m-knopf m-knopf-haupt"
              disabled={speichert || werte === null || Object.keys(fehler).length > 0}
              onClick={() => { if (werte) beiSpeichern(werte); }}
            >
              Speichern
            </button>
            <button
              type="button"
              className="m-knopf m-knopf-still"
              disabled={speichert || werte === null}
              onClick={() => setFormular(ausgang)}
            >
              Verwerfen
            </button>
          </>
        )}
      >
        <Feld beschriftung="Kabellänge" art="zahl" einheit="m" nachkomma={1}
              wert={formular.laengeM} beiAenderung={setzeZahl('laengeM')}
              fehler={fehler.laengeM}
              hinweis={z.laengeHerkunft === 'hand' ? 'von Hand gesetzt' : 'überschreibt die Schätzung'} />
        <Feld beschriftung="Kameraart" art="auswahl"
              wert={formular.kameraart} beiAenderung={setzeText('kameraart')}
              optionen={detail.kameraarten.map((k) => ({ wert: k, beschriftung: k }))}
              platzhalter="—" />
        <Feld beschriftung="PoE" art="zahl" einheit="W" nachkomma={1}
              wert={formular.poeW} beiAenderung={setzeZahl('poeW')}
              fehler={fehler.poeW} />
        <Feld beschriftung="PoE-Klasse" wert={formular.poeKlasse}
              beiAenderung={setzeText('poeKlasse')}
              platzhalter="z. B. Klasse 4 (802.3at)" />
        <Feld beschriftung="Öffnungswinkel" art="zahl" einheit="°"
              wert={formular.oeffnungswinkel} beiAenderung={setzeZahl('oeffnungswinkel')}
              fehler={fehler.oeffnungswinkel} />
        <Feld beschriftung="Blickrichtung" art="zahl" einheit="°"
              wert={formular.blickrichtung} beiAenderung={setzeZahl('blickrichtung')}
              fehler={fehler.blickrichtung}
              hinweis="0° = Projekt-Nord, im Uhrzeigersinn" />
        <Feld beschriftung="VLAN (Plan)" art="zahl" nachkomma={0}
              wert={formular.vlan} beiAenderung={setzeZahl('vlan')}
              fehler={fehler.vlan} />
        <Feld beschriftung="IP (Plan)" wert={formular.ipPlan}
              beiAenderung={setzeText('ipPlan')}
              hinweis="Planungswert, keine MAC, keine Zugangsdaten" />
        <Feld beschriftung="Bemerkung" art="textfeld" breite="voll" zeilen={2}
              wert={formular.bemerkung} beiAenderung={setzeText('bemerkung')} />
      </FormSection>

      {(z.laengeHerkunft === 'hand' || z.kameraartHerkunft === 'hand'
        || z.poeHerkunft === 'hand') && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {z.laengeHerkunft === 'hand' && (
            <button type="button" className="m-knopf" disabled={speichert}
                    onClick={() => zurueck('laengeM')}>
              Länge an den Lauf zurückgeben
            </button>
          )}
          {z.kameraartHerkunft === 'hand' && (
            <button type="button" className="m-knopf" disabled={speichert}
                    onClick={() => zurueck('kameraart')}>
              Kameraart an den Lauf zurückgeben
            </button>
          )}
          {z.poeHerkunft === 'hand' && (
            <button type="button" className="m-knopf" disabled={speichert}
                    onClick={() => zurueck('poe')}>
              PoE an den Lauf zurückgeben
            </button>
          )}
        </div>
      )}

      <Datenschutzhinweis text={detail.datenschutz} />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Seite
// ═══════════════════════════════════════════════════════════════════════════

export function ElektroCctvDetail() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { kameraId } = useParams<{ kameraId: string }>();

  const [detail, setDetail] = useState<CctvDetail | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  const laden = useCallback(() => {
    if (!projekt || !kameraId) return;
    setFehler(null);

    api.elektroCctvDetail(projekt.id, kameraId)
      .then(setDetail)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id, kameraId]);

  useEffect(() => { laden(); }, [laden]);

  const speichern = async (werte: CctvWerte) => {
    if (!projekt || !kameraId) return;
    setSpeichert(true);
    setMeldung(null);

    try {
      const a = await api.elektroCctvWerte(projekt.id, kameraId, werte);
      setMeldung(a.message);
      laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichert(false);
    }
  };

  const z = detail?.zeile;

  const kopf = (
    <PageHeader
      titel={z ? `Kamera ${z.kennung}` : 'Kamera'}
      beschreibung={z
        ? [z.geschoss, z.raumnummer, z.typ].filter(Boolean).join(' · ')
        : undefined}
      meta={z && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <SymKamera size={14} />
          <StatusBadge zustand={statusZustand(z.status)}
                       text={STATUS_TEXT[z.status] ?? z.status} klein />
        </span>
      )}
      aktionen={<Link className="m-knopf" to="../elektro-cctv">Zur Kameraliste</Link>}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Kamera konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!detail) {
    return (
      <>
        {kopf}
        <LoadingState text="Kamera wird geladen …" zeilen={6} spalten={2} />
      </>
    );
  }

  return (
    <>
      {kopf}

      {meldung && (
        <div className="hinweis" role="status" style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12 }}>{meldung}</div>
        </div>
      )}

      <CctvKamera detail={detail} beiSpeichern={speichern} speichert={speichert} />

      {/* Das Schema des Schranks, an dem die Kamera hängt (Etappe 3) - nur
          mit Schrank; ohne ihn gäbe es kein Blatt und der Knopf liefe auf 404. */}
      {projekt && detail.zeile.itSchrankId && detail.zeile.itSchrank && !detail.zeile.entfernt && (
        <CctvSchrankSchema
          api={api}
          projektId={projekt.id}
          itSchrankId={detail.zeile.itSchrankId}
          kennzeichen={detail.zeile.itSchrank}
        />
      )}
    </>
  );
}
